import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ScenarioBar from "../components/ScenarioBar";
import { setGroup } from "../redux/actions";
import { ROUTES } from "../utils/constants";
import { updateStats } from "../utils/helpers";

import "./TransitionScenarios.css";

const STEP_ORDER = [2, 3, 4, 5];

const SCENARIO_LABEL = {
	2: "Diagnóstico",
	3: "Priorización",
	4: "Implantación",
	5: "Gobierno y escalado",
};

function getRouteForScenario(scenarioNum) {
	if (scenarioNum === 1) return ROUTES.SCENARIO1_INTRO;
	if (scenarioNum === 2) return ROUTES.SCENARIO2_CONTEXT1;
	if (scenarioNum === 3) return ROUTES.SCENARIO3_CONTEXT1;
	if (scenarioNum === 4) return ROUTES.RESULTS;
	if (scenarioNum === 5) return ROUTES.SCENARIO5_CONTEXT1;
	return ROUTES.SCENARIO2_INTRO;
}

function stepStatus(stepId, activeScenario) {
	if (stepId < activeScenario) return "done";
	if (stepId === activeScenario) return "current";
	return "locked";
}

export default function TransitionScenarios() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const prevState = useLocation();
	const group = useSelector((state) => state?.group);
	const actualScenario = useSelector((state) => state?.group?.actualScenario);
	const rawActive = prevState?.state?.activeScenario ?? actualScenario;
	const activeScenario = Math.min(Math.max(Number(rawActive) || 0, 2), 6);

	const label = SCENARIO_LABEL[activeScenario] || SCENARIO_LABEL[2];

	const handleGoToScenario = async (scenarioNum) => {
		if (scenarioNum !== activeScenario) return;
		if (scenarioNum >= 2 && scenarioNum <= 5) {
			const prev = Array.isArray(group?.hubScenarioEntries)
				? group.hubScenarioEntries
				: [];
			const hubScenarioEntries = [
				...new Set([...prev, scenarioNum]),
			].sort((a, b) => a - b);
			const nextGroup = { ...group, hubScenarioEntries };
			dispatch(setGroup(nextGroup));
			try {
				await updateStats(nextGroup);
			} catch (e) {
				console.error("No se pudo guardar hubScenarioEntries", e);
			}
		}
		navigate(getRouteForScenario(scenarioNum));
	};

	const openScenario = () => handleGoToScenario(activeScenario);

	return (
		<div className="ts-hub-page ts-hub-page--brand">
			<div
				className="ts-hub-main"
				role="button"
				tabIndex={0}
				onClick={openScenario}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						openScenario();
					}
				}}
				aria-label={`Entrar al escenario: ${label}`}
			>
				<div className="ts-hub-card">
					<p className="ts-hub-eyebrow">Secuencia de escenarios</p>
					<h1 className="ts-hub-title">
						Ahora:{" "}
						<span className="ts-hub-title-accent">{label}</span>
					</h1>
					<p className="ts-hub-hint">
						Los bloques se desbloquean uno tras otro al cerrar cada escena.
						Pulsa para entrar al que está activo.
					</p>

					<div className="ts-hub-track" aria-hidden>
						{STEP_ORDER.map((stepId, index) => {
							const status = stepStatus(stepId, activeScenario);
							const prevId = index > 0 ? STEP_ORDER[index - 1] : null;
							const segmentDone =
								prevId !== null && prevId < activeScenario;

							return (
								<React.Fragment key={stepId}>
									{index > 0 ? (
										<div
											className={
												segmentDone
													? "ts-hub-joint ts-hub-joint--done"
													: "ts-hub-joint"
											}
											aria-hidden
										>
											<span className="ts-hub-joint-icon">›</span>
										</div>
									) : null}
									<div
										className={`ts-hub-step ts-hub-step--${status} ts-hub-step--n${stepId}`}
									>
										<div className="ts-hub-step-ring">
											{status === "done" ? (
												<span className="ts-hub-step-mark">✓</span>
											) : (
												<span className="ts-hub-step-num">{index + 1}</span>
											)}
										</div>
										<span className="ts-hub-step-label">
											{SCENARIO_LABEL[stepId]}
										</span>
									</div>
								</React.Fragment>
							);
						})}
					</div>
				</div>
			</div>

			<ScenarioBar
				activeScenario={activeScenario}
				onScenarioClick={handleGoToScenario}
			/>
		</div>
	);
}
